import styles from "./Modals.module.scss";
import React, { useRef, useState } from "react";
import type { Destination } from "@whatslly/types/messages-server/destinations";
import { DESTINATION_INTEGRATION_TYPES } from "@whatslly/types/messages-server/destinations";
import { FormElement } from "@whatslly/ui-components/v2/FormElements/Form";
import { FormikErrors, FormikProps } from "formik";
import type { PartialRecord } from "@whatslly/commons/typescriptUtils";
import { Label } from "@whatslly/ui-components/v2/BaseElements/Label";
import { useTranslation } from "react-i18next";
import { FormPasswordTextInput } from "@whatslly/ui-components/v2/FormElements/FormTextInput/FormPasswordTextInput";
import { FormTextInput } from "@whatslly/ui-components/v2/FormElements/FormTextInput";
import type { DestinationFormProps, FormDestinationDataType } from "../utils/DestinationsListTypes";
import { observer } from "mobx-react-lite";
import { useLocalStoreWithInjectedDependencies } from "@whatslly/commons/di";
import { DestinationsStore } from "../DestinationsStore";
import { DestinationsModalButtons } from "../DestinationsModalButtons";

const BASE_DATA: Destination = {
  type: "azure_openai" as const,
  integrationType: DESTINATION_INTEGRATION_TYPES.LLM_PROVIDER,
  settings: { apiKey: "", endpoint: "", deploymentName: "", apiVersion: "", enabledModels: [], defaultModel: "" },
};

export const AzureOpenAiModalForm: React.FC<DestinationFormProps<"azure_openai">> = observer(({ settings, onSubmitForm }) => {
  const { t } = useTranslation();
  const store = useLocalStoreWithInjectedDependencies(DestinationsStore);
  const formikContext = useRef<FormikProps<FormDestinationDataType<"azure_openai">>>(null);
  const initialValues = (settings || BASE_DATA.settings) as FormDestinationDataType<"azure_openai">;
  const [formikErrorHasErrors, setFormikErrorHasErrors] = useState(!initialValues.apiKey || !initialValues.endpoint || !initialValues.deploymentName);

  const validate = async (values: FormDestinationDataType<"azure_openai">) => {
    const errors: FormikErrors<PartialRecord<keyof FormDestinationDataType<"azure_openai">, string>> = {};
    if (!values.apiKey) {
      errors.apiKey = t("field_is_required");
    }
    if (!values.endpoint) {
      errors.endpoint = t("field_is_required");
    }
    if (!values.deploymentName) {
      errors.deploymentName = t("field_is_required");
    }
    setFormikErrorHasErrors(Object.keys(errors).length > 0);
    return errors;
  };

  const getFormValues = () => {
    const data = (formikContext.current?.values ?? {}) as FormDestinationDataType<"azure_openai">;
    return { ...BASE_DATA, settings: data };
  };

  const onSubmit = async () => {
    store.testConnection(t, getFormValues());
    const res = await store.testConnectionPromise;
    if (res?.success) {
      onSubmitForm(getFormValues());
    }
  };

  return (
    <>
      <FormElement initialValues={initialValues} validate={validate} onSubmit={onSubmit} formikRef={formikContext}>
        <div className={styles.field}>
          <Label className={styles.filedLabel} text={t("destinations_page.api_key")} />
          <FormPasswordTextInput name={"apiKey"} paddingInline="none" />
        </div>
        <div className={styles.field}>
          <Label className={styles.filedLabel} text={t("destinations_page.azure_endpoint")} />
          <FormTextInput name={"endpoint"} paddingInline="none" placeholder="https://your-resource.openai.azure.com" />
        </div>
        <div className={styles.field}>
          <Label className={styles.filedLabel} text={t("destinations_page.deployment_name")} />
          <FormTextInput name={"deploymentName"} paddingInline="none" />
        </div>
        <div className={styles.field}>
          <Label className={styles.filedLabel} text={t("destinations_page.api_version")} />
          <FormTextInput name={"apiVersion"} paddingInline="none" placeholder="2024-02-01" />
        </div>
        <DestinationsModalButtons formikContext={formikContext} formikErrorHasErrors={formikErrorHasErrors} store={store} />
      </FormElement>
    </>
  );
});
